/** @jsx jsx */
import { css, jsx } from '@compiled/react';
import { N20A } from '@atlaskit/theme/colors';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

const skeletonFill = token('color.skeleton', N20A);

export const skeletons = {
	OverviewCount: () => (
		<svg
			width="242"
			height="56"
			viewBox="0 0 242 56"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<defs>
				<linearGradient id="shimmer" x1="-100%" x2="100%" y1="0" y2="0">
					<stop offset="0%" stop-color="rgba(9, 30, 66, 0.06)">
						<animate attributeName="offset" values="-2; 1" dur="2s" repeatCount="indefinite" />
					</stop>
					<stop offset="50%" stop-color="rgba(9, 30, 66, 0.1)">
						<animate attributeName="offset" values="-1.5; 1.5" dur="2s" repeatCount="indefinite" />
					</stop>
					<stop offset="100%" stop-color="rgba(9, 30, 66, 0.06)">
						<animate attributeName="offset" values="-1; 2" dur="2s" repeatCount="indefinite" />
					</stop>
				</linearGradient>
			</defs>

			<rect width="40" height="40" rx="4" transform="translate(0 8)" fill={skeletonFill} />
			<rect width="100" height="20" transform="translate(48 8)" fill={skeletonFill} />
			<rect width="194" height="16" transform="translate(48 32)" fill={skeletonFill} />
		</svg>
	),
	StatusOverview: () => (
		<svg
			width="520"
			height="280"
			viewBox="0 0 520 280"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<rect
				width="166.353"
				height="20"
				transform="translate(0.803711 0.803955)"
				fill={skeletonFill}
			/>
			<rect
				width="520.392"
				height="20"
				transform="translate(0.803711 24.7648)"
				fill={skeletonFill}
			/>
			<g clip-path="url(#clip0_12833_49225)">
				<g clip-path="url(#clip1_12833_49225)">
					<path
						d="M281.414 158.259C281.414 212.163 237.716 255.862 183.811 255.862C129.906 255.862 86.208 212.163 86.208 158.259C86.208 104.354 129.906 60.6558 183.811 60.6558C237.716 60.6558 281.414 104.354 281.414 158.259ZM115.489 158.259C115.489 195.992 146.078 226.581 183.811 226.581C221.544 226.581 252.133 195.992 252.133 158.259C252.133 120.526 221.544 89.9367 183.811 89.9367C146.078 89.9367 115.489 120.526 115.489 158.259Z"
						fill={skeletonFill}
						mask="url(#path-1-inside-1_12833_49225)"
					/>
					<rect
						width="24.4008"
						height="16.8281"
						transform="translate(172.031 139.731)"
						fill={skeletonFill}
					/>
					<rect
						width="94.2374"
						height="16.8281"
						transform="translate(137.113 159.958)"
						fill={skeletonFill}
					/>
				</g>
			</g>
			<rect x="315" y="123.366" width="10.0969" height="10.0969" rx="1.68281" fill={skeletonFill} />
			<rect
				width="111.066"
				height="16.8281"
				transform="translate(331.895 120)"
				fill={skeletonFill}
			/>
			<rect x="315" y="153.656" width="10.0969" height="10.0969" rx="1.68281" fill={skeletonFill} />
			<rect
				width="111.066"
				height="16.8281"
				transform="translate(331.895 150.291)"
				fill={skeletonFill}
			/>
			<rect
				width="10.0969"
				height="10.0969"
				transform="translate(315 183.947)"
				fill={skeletonFill}
			/>
			<rect
				width="111.066"
				height="16.8281"
				transform="translate(331.895 180.581)"
				fill={skeletonFill}
			/>
		</svg>
	),
	PriorityBreakdown: () => (
		<svg
			width="520"
			height="280"
			viewBox="0 0 520 280"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<rect width="154" height="20" fill={skeletonFill} />
			<rect width="518" height="20" transform="translate(0 24)" fill={skeletonFill} />
			<g clip-path="url(#clip0_12833_49513)">
				<rect
					width="37.4502"
					height="11.9841"
					transform="translate(114.505 245.721)"
					fill={skeletonFill}
				/>
				<rect
					width="37.4502"
					height="11.9841"
					transform="translate(171.555 245.721)"
					fill={skeletonFill}
				/>
				<rect
					width="37.4502"
					height="11.9841"
					transform="translate(228.604 245.721)"
					fill={skeletonFill}
				/>
				<rect
					width="37.4502"
					height="11.9841"
					transform="translate(285.652 245.721)"
					fill={skeletonFill}
				/>
				<rect
					width="37.4502"
					height="11.9841"
					transform="translate(342.701 245.721)"
					fill={skeletonFill}
				/>
				<rect
					width="37.4502"
					height="11.9841"
					transform="translate(399.751 245.721)"
					fill={skeletonFill}
				/>
				<rect
					width="20.5535"
					height="91.9822"
					transform="translate(123.678 146.249)"
					fill={skeletonFill}
				/>
				<rect
					width="20.5535"
					height="129.181"
					transform="translate(180.595 109.051)"
					fill={skeletonFill}
				/>
				<rect
					width="20.5535"
					height="144.06"
					transform="translate(237.513 94.8473)"
					fill={skeletonFill}
				/>
				<rect
					width="20.5535"
					height="123.77"
					transform="translate(294.344 114.868)"
					fill={skeletonFill}
				/>
				<rect
					width="20.5535"
					height="86.5714"
					transform="translate(351.347 152.336)"
					fill={skeletonFill}
				/>
				<rect
					width="20.5535"
					height="110.243"
					transform="translate(408.264 128.664)"
					fill={skeletonFill}
				/>
			</g>
		</svg>
	),
	RecentActivity: () => (
		<svg
			width="520"
			height="280"
			viewBox="0 0 520 280"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<rect width="200" height="20" transform="translate(0 2)" fill={skeletonFill} />
			<rect width="494" height="20" transform="translate(0 28)" fill={skeletonFill} />
			<rect y="91" width="24" height="24" rx="12" fill={skeletonFill} />
			<rect width="419" height="24" transform="translate(28 89)" fill={skeletonFill} />
			<rect width="100" height="20" transform="translate(28 117)" fill={skeletonFill} />
			<rect width="60" height="20" transform="translate(28 141)" fill={skeletonFill} />
			<rect width="127" height="20" transform="translate(29 198)" fill={skeletonFill} />
			<rect y="170" width="24" height="24" rx="12" fill={skeletonFill} />
			<rect width="436" height="24" transform="translate(28 170)" fill={skeletonFill} />
			<rect y="228" width="24" height="24" rx="12" fill={skeletonFill} />
			<rect width="426" height="24" transform="translate(28 228)" fill={skeletonFill} />
			<rect width="129" height="20" transform="translate(28 256)" fill={skeletonFill} />
			<rect x="1" y="67" width="56" height="13" fill={skeletonFill} />
		</svg>
	),
	TypeOfWork: () => (
		<svg
			width="520"
			height="280"
			viewBox="0 0 520 280"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<rect width="114" height="20" fill={skeletonFill} />
			<rect width="509" height="20" transform="translate(0 24)" fill={skeletonFill} />
			<rect width="76" height="16" transform="translate(0 68)" fill={skeletonFill} />
			<rect width="189" height="16" transform="translate(152 68)" fill={skeletonFill} />
			<rect width="56" height="16" transform="translate(417 68)" fill={skeletonFill} />
			<rect width="105" height="24" transform="translate(0 99)" fill={skeletonFill} />
			<rect width="242" height="24" transform="translate(152 99)" fill={skeletonFill} />
			<rect width="30" height="20" transform="translate(433 101)" fill={skeletonFill} />
			<rect width="106" height="24" transform="translate(0 139)" fill={skeletonFill} />
			<rect width="242" height="24" transform="translate(152 139)" fill={skeletonFill} />
			<rect width="30" height="20" transform="translate(433 141)" fill={skeletonFill} />
			<rect width="108" height="24" transform="translate(0 179)" fill={skeletonFill} />
			<rect width="242" height="24" transform="translate(152 179)" fill={skeletonFill} />
			<rect width="30" height="20" transform="translate(433 181)" fill={skeletonFill} />
		</svg>
	),
	TeamWorkload: () => (
		<svg
			width="520"
			height="280"
			viewBox="0 0 520 280"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<rect width="168" height="20" fill={skeletonFill} />
			<rect width="505" height="20" transform="translate(0 24)" fill={skeletonFill} />
			<rect width="76" height="16" transform="translate(11 68)" fill={skeletonFill} />
			<rect width="189" height="16" transform="translate(166 68)" fill={skeletonFill} />
			<rect width="56" height="16" transform="translate(434 68)" fill={skeletonFill} />
			<rect x="4" y="100" width="24" height="24" rx="12" fill={skeletonFill} />
			<rect width="108" height="20" transform="translate(36 102)" fill={skeletonFill} />
			<rect width="242" height="24" transform="translate(160 100)" fill={skeletonFill} />
			<rect width="30" height="20" transform="translate(440 102)" fill={skeletonFill} />
			<rect x="4" y="140" width="24" height="24" rx="12" fill={skeletonFill} />
			<rect width="108" height="20" transform="translate(36 142)" fill={skeletonFill} />
			<rect width="242" height="24" transform="translate(160 140)" fill={skeletonFill} />
			<rect width="30" height="20" transform="translate(440 142)" fill={skeletonFill} />
			<rect x="4" y="180" width="24" height="24" rx="12" fill={skeletonFill} />
			<rect width="108" height="20" transform="translate(36 182)" fill={skeletonFill} />
			<g clip-path="url(#clip0_11653_70962)">
				<rect width="242" height="24" transform="translate(160 180)" fill={skeletonFill} />
			</g>
			<rect width="30" height="20" transform="translate(440 182)" fill={skeletonFill} />
			<rect x="4" y="220" width="24" height="24" rx="12" fill={skeletonFill} />
			<rect width="108" height="20" transform="translate(36 222)" fill={skeletonFill} />
			<rect width="242" height="24" transform="translate(160 220)" fill={skeletonFill} />
			<rect width="30" height="20" transform="translate(440 222)" fill={skeletonFill} />
		</svg>
	),
	EpicProgress: () => (
		<svg
			width="520"
			height="280"
			viewBox="0 0 520 280"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<g clip-path="url(#clip0_11653_70962)">
				<rect y="166" width="24" height="24" rx="3" fill={skeletonFill} />
				<rect width="317" height="20" transform="translate(28 168)" fill={skeletonFill} />
				<path
					opacity="0.85"
					d="M0 196C0 194.895 0.89543 194 2 194H245V216H2C0.895432 216 0 215.105 0 214V196Z"
					fill={skeletonFill}
				/>
				<g clip-path="url(#clip1_11653_70962)">
					<rect opacity="0.85" x="245" y="194" width="138" height="22" fill={skeletonFill} />
				</g>
				<g clip-path="url(#clip2_11653_70962)">
					<rect opacity="0.85" x="383" y="194" width="118" height="22" fill={skeletonFill} />
				</g>
				<rect y="100" width="24" height="24" rx="3" fill={skeletonFill} />
				<rect width="339" height="20" transform="translate(28 102)" fill={skeletonFill} />
				<g clip-path="url(#clip3_11653_70962)">
					<path
						opacity="0.85"
						d="M0 130C0 128.895 0.895431 128 2 128H38V150H2C0.89543 150 0 149.105 0 148V130Z"
						fill={skeletonFill}
					/>
				</g>
				<g clip-path="url(#clip4_11653_70962)">
					<rect opacity="0.85" x="38" y="128" width="276" height="22" fill={skeletonFill} />
				</g>
				<g clip-path="url(#clip5_11653_70962)">
					<rect opacity="0.85" x="314" y="128" width="187" height="22" fill={skeletonFill} />
				</g>
				<rect y="232" width="24" height="24" rx="3" fill={skeletonFill} />
				<rect width="339" height="20" transform="translate(28 234)" fill={skeletonFill} />
				<g clip-path="url(#clip6_11653_70962)">
					<path
						opacity="0.85"
						d="M0 262C0 260.895 0.895431 260 2 260H285V282H2C0.895433 282 0 281.105 0 280V262Z"
						fill={skeletonFill}
					/>
				</g>
				<g clip-path="url(#clip7_11653_70962)">
					<rect opacity="0.85" x="284.753" y="260" width="188" height="22" fill={skeletonFill} />
				</g>
				<g clip-path="url(#clip8_11653_70962)">
					<rect opacity="0.85" x="472.938" y="260" width="28" height="22" fill={skeletonFill} />
				</g>
			</g>
			<rect width="168" height="24" fill={skeletonFill} />
			<rect width="501" height="20" transform="translate(0 28)" fill={skeletonFill} />
			<rect y="70.5" width="12" height="12" rx="2" fill={skeletonFill} />
			<rect width="33" height="16" transform="translate(20 68.5)" fill={skeletonFill} />
			<rect x="69" y="70.5" width="12" height="12" rx="2" fill={skeletonFill} />
			<rect width="67" height="16" transform="translate(89 68.5)" fill={skeletonFill} />
			<rect x="172" y="70.5" width="12" height="12" rx="2" fill={skeletonFill} />
			<rect width="31" height="16" transform="translate(192 68.5)" fill={skeletonFill} />
		</svg>
	),
	RelatedProjects: () => (
		<svg
			width="520"
			height="280"
			viewBox="0 0 520 280"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			css={skeletonStyles}
		>
			<rect width="476" height="8" rx="4" fill={skeletonFill} />
			<rect y="16" width="320" height="8" rx="4" fill={skeletonFill} />
			<rect y="56" width="48" height="48" rx="4" fill={skeletonFill} />
			<rect x="64" y="68" width="160" height="8" rx="4" fill={skeletonFill} />
			<rect x="64" y="84" width="80" height="8" rx="4" fill={skeletonFill} />
			<rect x="252" y="56" width="48" height="48" rx="4" fill={skeletonFill} />
			<rect x="316" y="68" width="160" height="8" rx="4" fill={skeletonFill} />
			<rect x="316" y="84" width="80" height="8" rx="4" fill={skeletonFill} />
			<rect y="136" width="48" height="48" rx="4" fill={skeletonFill} />
			<rect x="64" y="148" width="160" height="8" rx="4" fill={skeletonFill} />
			<rect x="64" y="164" width="80" height="8" rx="4" fill={skeletonFill} />
			<rect x="252" y="136" width="48" height="48" rx="4" fill={skeletonFill} />
			<rect x="316" y="148" width="160" height="8" rx="4" fill={skeletonFill} />
			<rect x="316" y="164" width="80" height="8" rx="4" fill={skeletonFill} />
			<rect y="216" width="48" height="48" rx="4" fill={skeletonFill} />
			<rect x="64" y="228" width="160" height="8" rx="4" fill={skeletonFill} />
			<rect x="64" y="244" width="80" height="8" rx="4" fill={skeletonFill} />
			<rect x="252" y="216" width="48" height="48" rx="4" fill={skeletonFill} />
			<rect x="316" y="228" width="160" height="8" rx="4" fill={skeletonFill} />
			<rect x="316" y="244" width="80" height="8" rx="4" fill={skeletonFill} />
			<rect y="296" width="180" height="16" rx="8" fill={skeletonFill} />
		</svg>
	),
	CsatOverTime: () => {
		// TODO https://hello.jira.atlassian.cloud/browse/CORCAP-2966
		return <Text>Needs design and implementation for the loading state</Text>;
	},
};
const skeletonStyles = css({ width: '100%', height: '100%' });
